/**
 * THE PREVIEW LINK AS A QR CODE, DRAWN IN THE PAGE (register R-032, lens finding C2-08).
 *
 * A desktop author who wants the draft on their phone points the phone's camera at this, instead of
 * moving a long address across by hand. ./PreviewLinkNotice.tsx shows it under the link.
 *
 * ★ DRAWN HERE FROM THE ENCODER'S MODULE MATRIX, NEVER FETCHED. The URL is a bearer credential -
 * anyone holding it reads the unpublished draft (`PreviewLink` in authors-api.yaml) - so no image
 * service may see it, as the legacy site's qrserver did. `qrcode-generator` (MIT, no dependencies,
 * pinned in web/authors/package.json) computes the matrix in the page, and this file reads it
 * module by module with `isDark` and writes React SVG elements. Not the library's `createSvgTag`,
 * `createImgTag` or `createDataURL`: they return markup or a data URL, which would need
 * `dangerouslySetInnerHTML` or an <img>, and drawing squares needs neither.
 *
 * WHAT A PHONE NEEDS TO READ IT, and so what is fixed here:
 *   - error-correction M: 15% of the code can be lost to glare or a smudge and it still reads. L
 *     (7%) saves little on a link this short; Q and H make the code denser for no gain on a screen;
 *   - a quiet zone of four modules on every side, which the QR standard (ISO/IEC 18004) requires -
 *     without it a reader can take the page around the code for part of the code;
 *   - dark modules on a LIGHT plate in both colour schemes. Many readers cannot read an inverted
 *     code, so the plate is Mantine's `white` and the modules its `black` (web/ui's tokens.ts,
 *     `absolute`), which are the same in light and dark - never the text and page colours, which
 *     swap in dark. `forced-color-adjust: none` keeps them in a forced-colours (high-contrast) mode;
 *   - four CSS pixels a module, so a longer link grows the code rather than shrinking its modules:
 *     the fixture's 48-character link is version 4, (33 + 8) x 4 = 164 pixels square.
 *
 * THE BYTES ARE THE LINK'S UTF-8. The library's byte mode keeps only the low byte of each UTF-16
 * unit, so a non-ASCII character would be encoded as some other character. `PreviewLink.url` is
 * `format: uri`, which is ASCII, so for a link the contract allows this changes nothing; it keeps a
 * slip in what the server returns from drawing a code that opens somewhere else.
 *
 * A LINK NO QR VERSION HOLDS (over 2,331 bytes at M, version 40's capacity) draws no code rather
 * than throwing out of the render and taking the editor with it; the link and its copy stay.
 */
import { useMemo } from 'react';
import { Group, Text } from '@mantine/core';
import qrcode from 'qrcode-generator';
import { useTranslation } from '../../../i18n';

/** Modules of plate on every side of the code: the quiet zone ISO/IEC 18004 requires. */
const QUIET_ZONE = 4;

/** CSS pixels a module is drawn at. */
const MODULE_PX = 4;

export interface PreviewQrCodeProps {
  /** The minted link - `mintDraftPreviewLink`'s `url`. */
  readonly url: string;
}

/** `text` as UTF-8, one character per byte: what the library's byte mode encodes byte for byte. */
function utf8ByteString(text: string): string {
  let bytes = '';
  for (const byte of new TextEncoder().encode(text)) {
    bytes += String.fromCharCode(byte);
  }
  return bytes;
}

interface Drawing {
  /** The side of the plate in modules: the code's own modules plus the quiet zone twice. */
  readonly side: number;
  /** One unit square per dark module, at its column and row shifted by the quiet zone. */
  readonly modules: string;
}

function drawingOf(url: string): Drawing | null {
  const code = qrcode(0, 'M');
  code.addData(utf8ByteString(url), 'Byte');
  try {
    code.make();
  } catch {
    // The library throws a bare string, "code length overflow", when no version holds the data.
    return null;
  }
  const count = code.getModuleCount();
  let modules = '';
  for (let row = 0; row < count; row += 1) {
    for (let col = 0; col < count; col += 1) {
      if (code.isDark(row, col)) {
        modules += `M${col + QUIET_ZONE} ${row + QUIET_ZONE}h1v1h-1z`;
      }
    }
  }
  return { side: count + 2 * QUIET_ZONE, modules };
}

export function PreviewQrCode({ url }: PreviewQrCodeProps) {
  const { t } = useTranslation('articles');
  const drawing = useMemo(() => drawingOf(url), [url]);
  if (drawing === null) {
    return null;
  }
  const { side, modules } = drawing;

  return (
    <Group gap="sm" wrap="wrap" align="center">
      <svg
        role="img"
        aria-label={t('editor.preview.qrLabel')}
        viewBox={`0 0 ${side} ${side}`}
        width={side * MODULE_PX}
        height={side * MODULE_PX}
        shapeRendering="crispEdges"
        style={{ display: 'block', maxWidth: '100%', height: 'auto', forcedColorAdjust: 'none' }}
      >
        <rect width={side} height={side} style={{ fill: 'var(--mantine-color-white)' }} />
        <path d={modules} style={{ fill: 'var(--mantine-color-black)' }} />
      </svg>
      <Text size="sm">{t('editor.preview.qrNote')}</Text>
    </Group>
  );
}
